import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { QueryParams } from '@/model';
import { VideoService } from '../instances/video';
import { VideoConverter } from '@/converters';

export const useVideo = (params?: QueryParams) => {
  const query = useQuery(
    ['GET_VIDEO', params],
    async () => {
      const [err, data] = await VideoService.getVideos(params);
      if (err) {
        throw err;
      }
      return {
        data: VideoConverter.toDomainFromPaginatedResponse(data),
        meta: data.meta,
      };
    },
    {
      refetchOnWindowFocus: false,
    },
  );

  const forceRefetch = useCallback(() => {
    query.remove();
    query.refetch();
  }, [query]);

  const returnQuery = useMemo(() => ({ ...query, forceRefetch }), [forceRefetch, query]);
  return returnQuery;
};

export const useVideoById = (id: string) => {
  const query = useQuery(
    ['GET_VIDEO_BY_ID', id],
    async () => {
      const [err, data] = await VideoService.getVideoById(id);
      if (err) {
        throw err;
      }
      return VideoConverter.toDomainFromByIdResponse(data);
    },
    {
      refetchOnWindowFocus: false,
      enabled: !!id,
    },
  );

  const forceRefetch = useCallback(() => {
    query.remove();
    query.refetch();
  }, [query]);

  const returnQuery = useMemo(() => ({ ...query, forceRefetch }), [forceRefetch, query]);
  return returnQuery;
};

export const usePostVideoMutation = () => {
  return useMutation({
    mutationKey: ['POST_VIDEO'],
    mutationFn: async (payload: any) => {
      const [err, data] = await VideoService.postVideo(payload);
      if (err) {
        throw err;
      }
      return VideoConverter.toDomainFromPostResponse(data);
    },
  });
};

export const usePutVideoMutation = () => {
  return useMutation({
    mutationKey: ['PUT_VIDEO'],
    mutationFn: async ({ id, payload }: any) => {
      const [err, data] = await VideoService.putVideo(id, payload);
      if (err) {
        throw err;
      }
      return VideoConverter.toDomainFromPutResponse(data);
    },
  });
};

export const useDeleteVideoMutation = () => {
  return useMutation({
    mutationKey: ['DELETE_VIDEO'],
    mutationFn: async (id: string) => {
      const [err] = await VideoService.deleteVideo(id);
      if (err) {
        throw err;
      }
      return;
    },
  });
};

export const useGetVideoById = () => {
  return useMutation({
    mutationKey: ['GET_VIDEO_BY_ID'],
    mutationFn: async (uuid: string) => {
      const [err, data] = await VideoService.getVideoById(uuid);
      if (err) {
        throw err;
      }
      return VideoConverter.toDomainFromByIdResponse(data);
    },
  });
};

export const usePutLiveVideoMutation = () => {
  return useMutation({
    mutationKey: ['PUT_LIVE_VIDEO'],
    mutationFn: async ({ payload }: any) => {
      const [err, data] = await VideoService.putLiveVideo(payload);
      if (err) {
        throw err;
      }
      return VideoConverter.toDomainFromPutResponse(data);
    },
  });
};

export const useDeleteLiveVideoMutation = () => {
  return useMutation({
    mutationKey: ['DELETE_LIVE_VIDEO'],
    mutationFn: async () => {
      const [err] = await VideoService.deleteLiveVideo();
      if (err) {
        throw err;
      }
      return;
    },
  });
};
